import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { getTenderByGuid, getActiveTenders } from '../../api/tenderService';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { FaCalendarAlt, FaBuilding, FaMapMarkerAlt, FaFileDownload, FaInfoCircle } from 'react-icons/fa';

const TendersDetail = () => {
  const { urlTag } = useParams();
  const location = useLocation();
  const guidFromState = location.state?.guid;
  const [tender, setTender] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTenderDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      let guid = guidFromState;

      // Eğer state'den guid gelmemişse (direkt URL girişi), urlTag ile bul
      if (!guid && urlTag) {
        console.log('GUID not found in state, fetching by urlTag:', urlTag);
        const allTendersResponse = await getActiveTenders(1, 1000);
        
        if (allTendersResponse.isSuccess && allTendersResponse.data?.items) {
          const foundTender = allTendersResponse.data.items.find(
            item => item.urlTag === urlTag
          );
          
          if (foundTender) {
            guid = foundTender.guid;
            console.log('Found tender with guid:', guid);
          } else {
            console.log('Tender not found with urlTag:', urlTag);
            setError('İhale bulunamadı');
            setLoading(false);
            return;
          }
        } else {
          console.log('Failed to fetch tenders list');
          setError('İhaleler yüklenirken bir hata oluştu');
          setLoading(false);
          return;
        }
      }

      if (!guid) {
        console.log('No guid available');
        setError('İhale bilgisi bulunamadı');
        setLoading(false);
        return;
      }

      console.log('Fetching tender detail for guid:', guid);
      const response = await getTenderByGuid(guid);
      console.log('Tender detail response:', response);
      
      if (response.isSuccess && response.data) {
        setTender(response.data);
        setError(null);
      } else {
        setError(`İhale detayı yüklenirken bir hata oluştu: ${JSON.stringify(response)}`);
      }
    } catch (error) {
      console.error('Error in fetchTenderDetail:', error);
      setError(`İhale detayı yüklenirken bir hata oluştu: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  }, [urlTag, guidFromState]);

  useEffect(() => {
    fetchTenderDetail();
  }, [fetchTenderDetail]);

  const formatDate = (dateString) => {
    try {
      if (!dateString || dateString === '0001-01-01T00:00:00' || dateString.includes('0001-01-01')) {
        return 'Tarih belirtilmemiş';
      }
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Geçersiz tarih';
      }
      return format(date, 'dd MMMM yyyy HH:mm', { locale: tr });
    } catch (error) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="tenders-detail-page">
        <div className="container py-5 tender-detail-container">
          <div className="loading">Yükleniyor...</div>
        </div>
      </div>
    );
  }

  if (error || !tender) {
    return (
      <div className="tenders-detail-page">
        <div className="container py-5 tender-detail-container">
          <div className="alert alert-danger">{error || 'İhale bulunamadı'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="tenders-detail-page">
      <div className="container py-5 tender-detail-container">
        <div className="row">
          <div className="col-md-12">
            <div className="tenders-detail-card">
              <div className="detail-header">
                <h2>{tender.header}</h2>
                
                <div className="detail-meta-grid">
                  <div className="meta-item">
                    <FaBuilding />
                    <span className="meta-label">Birim:</span>
                    <span>{tender.department || 'Belirtilmemiş'}</span>
                  </div>
                  
                  <div className="meta-item">
                    <FaCalendarAlt />
                    <span className="meta-label">Tarih/Saat:</span>
                    <span>{formatDate(tender.date)}</span>
                  </div>

                  <div className="meta-item">
                    <FaInfoCircle />
                    <span className="meta-label">Durum:</span>
                    <span className="status-badge">{tender.statusName}</span>
                  </div>

                  {tender.address && (
                    <div className="meta-item">
                      <FaMapMarkerAlt />
                      <span className="meta-label">Adres:</span>
                      <span>{tender.address}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="detail-body-section">
                <h3>İhale Metni</h3>
                <div 
                  className="detail-text" 
                  dangerouslySetInnerHTML={{ __html: tender.detail || '' }}
                />
              </div>

              {tender.fileUrl && (
                <div className="detail-action-section">
                  <a href={tender.fileUrl} target="_blank" rel="noopener noreferrer" className="download-btn">
                    <FaFileDownload />
                    <span>İhale Dosyasını İndir</span>
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TendersDetail;
